import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { api } from '../api';
import { useAdmin } from '../auth';
import { Btn, Empty, ErrorBox, Loading, Modal, fmtDate, useAction } from '../ui';

interface Banner { id: string; title: string; imageUrl: string | null; linkUrl: string | null; placement: string; locale: string | null; isActive: boolean; sortOrder: number; startsAt: string | null; endsAt: string | null }

export function BannersPage() {
  const qc = useQueryClient();
  const action = useAction();
  const { data, isLoading, error } = useQuery({ queryKey: ['banners'], queryFn: () => api<{ items: Banner[] }>('/admin/banners').then((r) => r.items) });
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ title: '', linkUrl: '', locale: '' });
  const refresh = () => void qc.invalidateQueries({ queryKey: ['banners'] });
  const upload = (id: string, file: File) =>
    action.run(async () => {
      const fd = new FormData();
      fd.append('file', file);
      await api(`/admin/banners/${id}/image`, { method: 'POST', body: fd });
      refresh();
    });
  return (
    <div>
      <h1>Баннеры</h1>
      <p className="muted">Баннеры показываются на главной Mini App (не более 5 активных). Ссылки — только https.</p>
      <div className="toolbar"><Btn onClick={() => setCreating(true)}>Новый баннер</Btn></div>
      {action.error ? <ErrorBox error={action.error} /> : null}
      {isLoading ? <Loading /> : error ? <ErrorBox error={error} /> : !data?.length ? <Empty text="Баннеров нет" /> : (
        <table>
          <thead><tr><th>Изображение</th><th>Заголовок</th><th>Язык</th><th>Активен</th><th /></tr></thead>
          <tbody>
            {data.map((b) => (
              <tr key={b.id}>
                <td>{b.imageUrl ? <img className="banner-thumb" src={b.imageUrl} alt="" /> : <span className="muted">нет</span>}<br /><input type="file" accept="image/jpeg,image/png,image/webp" onChange={(e) => e.target.files?.[0] && void upload(b.id, e.target.files[0])} /></td>
                <td>{b.title}<div className="muted">{b.linkUrl}</div></td>
                <td>{b.locale ?? 'все'}</td>
                <td><input type="checkbox" checked={b.isActive} onChange={(e) => void action.run(async () => { await api(`/admin/banners/${b.id}`, { method: 'PATCH', json: { isActive: e.target.checked } }); refresh(); })} /></td>
                <td><Btn kind="danger" onClick={() => void action.run(async () => { if (window.confirm('Удалить баннер?')) { await api(`/admin/banners/${b.id}`, { method: 'DELETE' }); refresh(); } })}>Удалить</Btn></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {creating ? (
        <Modal title="Новый баннер" onClose={() => setCreating(false)}>
          <label className="field">Заголовок (для доступности)<input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></label>
          <label className="field">Ссылка (https://…)<input value={form.linkUrl} onChange={(e) => setForm({ ...form, linkUrl: e.target.value })} /></label>
          <label className="field">Язык<select value={form.locale} onChange={(e) => setForm({ ...form, locale: e.target.value })}><option value="">Все</option><option value="uz">uz</option><option value="kaa">kaa</option><option value="ru">ru</option></select></label>
          <Btn busy={action.busy} onClick={() => void action.run(async () => { await api('/admin/banners', { method: 'POST', json: { title: form.title, linkUrl: form.linkUrl || null, locale: form.locale || null } }); setCreating(false); refresh(); })}>Создать (затем загрузите изображение)</Btn>
          {action.error ? <ErrorBox error={action.error} /> : null}
        </Modal>
      ) : null}
    </div>
  );
}

export function PromotionsPage() {
  const qc = useQueryClient();
  const action = useAction();
  const [form, setForm] = useState({ listingId: '', type: 'vip', days: 3 });
  const { data, isLoading, error } = useQuery({
    queryKey: ['promotions'],
    queryFn: () => api<{ items: { id: string; type: string; status: string; source: string; startsAt: string; endsAt: string | null; listing: { id: string; title: string }; createdBy: string | null }[] }>('/admin/promotions').then((r) => r.items),
  });
  return (
    <div>
      <h1>Продвижение</h1>
      <p className="warn">Онлайн-оплата не подключена. Продвижение назначается вручную и работает, только если в настройках включено «Продвижение».</p>
      <div className="toolbar">
        <input placeholder="ID объявления" value={form.listingId} onChange={(e) => setForm({ ...form, listingId: e.target.value.replace(/\D/g, '') })} />
        <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}><option value="bump">Поднятие</option><option value="vip">VIP</option><option value="highlight">Выделение</option></select>
        {form.type !== 'bump' ? <input type="number" min={1} max={90} value={form.days} onChange={(e) => setForm({ ...form, days: Number(e.target.value) })} /> : null}
        <Btn busy={action.busy} disabled={!form.listingId} onClick={() => void action.run(async () => { await api('/admin/promotions', { method: 'POST', json: form }); void qc.invalidateQueries({ queryKey: ['promotions'] }); })}>Назначить</Btn>
      </div>
      {action.error ? <ErrorBox error={action.error} /> : null}
      {isLoading ? <Loading /> : error ? <ErrorBox error={error} /> : !data?.length ? <Empty /> : (
        <table>
          <thead><tr><th>Объявление</th><th>Тип</th><th>Статус</th><th>Период</th><th>Кто</th><th /></tr></thead>
          <tbody>
            {data.map((p) => (
              <tr key={p.id}>
                <td>#{p.listing.id} {p.listing.title}</td><td>{p.type}</td><td>{p.status}</td><td>{fmtDate(p.startsAt)} — {fmtDate(p.endsAt)}</td><td>{p.createdBy}</td>
                <td>{p.status === 'active' ? <Btn kind="ghost" onClick={() => void action.run(async () => { await api(`/admin/promotions/${p.id}/cancel`, { method: 'POST' }); void qc.invalidateQueries({ queryKey: ['promotions'] }); })}>Отменить</Btn> : null}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

interface Settings {
  autoPublish: boolean; maxActiveListingsPerUser: number; dailyPublishLimit: number; dailyReportLimit: number; hourlyInquiryLimit: number;
  listingTtlDays: number; expiryReminderDays: number; draftTtlDays: number; legalVersion: string; enabledLocales: string[]; currencyLabels: Record<'uz' | 'kaa' | 'ru', string>; adminTwoFactorRequired: boolean; bannedWords: string[]; promotionsEnabled: boolean; paymentsEnabled: boolean;
  promotionTariffs: { code: string; type: 'bump' | 'vip' | 'highlight' | 'business'; priceSum: number; priceStars: number; durationDays: number }[];
  chatEnabled: boolean; hourlyChatStartLimit: number; dailyChatMessageLimit: number;
  channelAutopost: boolean; channelChatId: string; channelLocale: 'uz' | 'kaa' | 'ru'; dailyDigest: boolean;
}

interface ChannelCheck { ok: boolean; title?: string; username?: string; canPost?: boolean; canEdit?: boolean; error?: string }
interface ChannelStats { queued: number; posted: number; failed: number; lastPostedAt: string | null; lastError: string | null }

/** Telegram-канал (автопостинг) и ежедневная сводка. Сохраняются общей кнопкой «Сохранить» внизу страницы. */
function ChannelSection({ f, setF }: { f: Settings; setF: (s: Settings) => void }) {
  const action = useAction();
  const [check, setCheck] = useState<ChannelCheck | null>(null);
  const [digestSent, setDigestSent] = useState(false);
  const stats = useQuery({ queryKey: ['channel-stats'], queryFn: () => api<ChannelStats>('/admin/channel/stats'), refetchInterval: 60_000 });
  const runCheck = () =>
    action.run(async () => {
      setCheck(null);
      setCheck(await api<ChannelCheck>('/admin/channel/check', { method: 'POST', json: { chatId: f.channelChatId.trim() } }));
    });
  const sendDigest = () =>
    action.run(async () => {
      await api('/admin/digest/send', { method: 'POST' });
      setDigestSent(true);
      setTimeout(() => setDigestSent(false), 4000);
    });
  const s = stats.data;
  return (
    <>
      <h3>Telegram-канал</h3>
      <p className="muted small">
        Одобренные объявления публикуются в канал по одному в минуту: фото, название, цена, место и кнопка «Открыть». Проданные и снятые помечаются в
        канале автоматически. Бот должен быть администратором канала с правами «Публикация сообщений» и «Редактирование сообщений».
      </p>
      <div className="grid2">
        <label className="field">
          Канал (@username или id -100…)
          <input value={f.channelChatId} placeholder="@qaraqalpaq_market" onChange={(e) => { setCheck(null); setF({ ...f, channelChatId: e.target.value }); }} />
        </label>
        <label className="field">
          Язык подписей в канале
          <select value={f.channelLocale} onChange={(e) => setF({ ...f, channelLocale: e.target.value as Settings['channelLocale'] })}>
            <option value="kaa">Каракалпакский</option>
            <option value="uz">Узбекский</option>
            <option value="ru">Русский</option>
          </select>
        </label>
      </div>
      <div className="row">
        <Btn kind="secondary" busy={action.busy} disabled={!f.channelChatId.trim()} onClick={() => void runCheck()}>Проверить канал</Btn>
        {check ? (
          check.ok ? <span className="ok">✓ {check.title ?? 'Канал'}{check.username ? ` (@${check.username})` : ''}: бот может публиковать и редактировать</span>
            : <span className="warn" role="alert">{check.title ? `${check.title}: ` : ''}{check.error ?? 'Нет доступа'}</span>
        ) : null}
      </div>
      <label className="check">
        <input type="checkbox" checked={f.channelAutopost} disabled={!f.channelChatId.trim() && !f.channelAutopost} onChange={(e) => setF({ ...f, channelAutopost: e.target.checked })} />{' '}
        Публиковать новые объявления в канал
      </label>
      {s ? (
        <p className="muted small">
          В очереди: {s.queued} · опубликовано: {s.posted}{s.failed ? <> · <b>не удалось: {s.failed}</b></> : null}
          {s.lastPostedAt ? ` · последний пост ${fmtDate(s.lastPostedAt)}` : ''}
          {s.lastError ? <><br />Последняя ошибка: {s.lastError}</> : null}
        </p>
      ) : null}
      <h3>Ежедневная сводка</h3>
      <label className="check"><input type="checkbox" checked={f.dailyDigest} onChange={(e) => setF({ ...f, dailyDigest: e.target.checked })} /> Каждое утро в 9:00 присылать сводку за прошедшие сутки в Telegram (в чат оповещений)</label>
      <div className="row">
        <Btn kind="secondary" busy={action.busy} onClick={() => void sendDigest()}>Прислать сводку за сегодня сейчас</Btn>
        {digestSent ? <span className="ok">Отправлено — проверьте Telegram</span> : null}
      </div>
      {action.error ? <ErrorBox error={action.error} /> : null}
    </>
  );
}

export function SettingsPage() {
  const { can } = useAdmin();
  const action = useAction();
  const { data, isLoading, error, refetch } = useQuery({ queryKey: ['settings'], queryFn: () => api<Settings>('/admin/settings') });
  const [f, setF] = useState<Settings | null>(null);
  const [words, setWords] = useState('');
  const [saved, setSaved] = useState(false);
  useEffect(() => {
    if (data) {
      setF(data);
      setWords(data.bannedWords.join('\n'));
    }
  }, [data]);
  if (isLoading || !f) return error ? <ErrorBox error={error} /> : <Loading />;
  const num = (k: keyof Settings, label: string) => (
    <label className="field">{label}<input type="number" min={1} value={f[k] as number} onChange={(e) => setF({ ...f, [k]: Number(e.target.value) })} /></label>
  );
  const save = () =>
    action.run(async () => {
      if (data && f.legalVersion !== data.legalVersion && !window.confirm('Версия правил изменена: всем пользователям будет показан экран согласия. Продолжить?')) return;
      const { promotionTariffs, paymentsEnabled, adminTwoFactorRequired, ...rest } = f;
      const body: Partial<Settings> = { ...rest, bannedWords: words.split('\n').map((w) => w.trim()).filter(Boolean) };
      if (can('tariffs.manage')) body.promotionTariffs = promotionTariffs;
      if (can('tariffs.manage') && data && paymentsEnabled !== data.paymentsEnabled) {
        if (paymentsEnabled && !window.confirm('Пользователи смогут покупать продвижение за Telegram Stars. Оферта и правила возврата опубликованы? Включить?')) return;
        body.paymentsEnabled = paymentsEnabled;
      }
      // Политику 2FA меняет только управляющий администраторами (сервер проверяет то же).
      if (can('admins.manage') && data && adminTwoFactorRequired !== data.adminTwoFactorRequired) {
        if (adminTwoFactorRequired && !window.confirm('Все администраторы без 2FA смогут работать только после её подключения. Включить?')) return;
        body.adminTwoFactorRequired = adminTwoFactorRequired;
      }
      setF(await api<Settings>('/admin/settings', { method: 'PUT', json: body }));
      setSaved(true);
      void refetch();
      setTimeout(() => setSaved(false), 2500);
    });
  return (
    <div>
      <h1>Настройки площадки</h1>
      <h3>Модерация</h3>
      <label className="check"><input type="checkbox" checked={f.autoPublish} onChange={(e) => setF({ ...f, autoPublish: e.target.checked })} /> Автопубликация без предварительной модерации (объявления со стоп-словами всё равно попадут в очередь)</label>
      <label className="field">Стоп-слова (по одному в строке; сравнение без учёта регистра и алфавита)<textarea rows={6} value={words} onChange={(e) => setWords(e.target.value)} /></label>
      <h3>Лимиты</h3>
      <div className="grid2">
        {num('dailyPublishLimit', 'Публикаций в сутки на пользователя')}
        {num('maxActiveListingsPerUser', 'Активных объявлений на пользователя')}
        {num('dailyReportLimit', 'Жалоб в сутки на пользователя')}
        {num('hourlyInquiryLimit', 'Обращений в час на пользователя')}
        {num('listingTtlDays', 'Срок публикации, дней')}
        <label className="field">Напоминание до окончания срока, дней (0 — выкл.)<input type="number" min={0} max={30} value={f.expiryReminderDays} onChange={(e) => setF({ ...f, expiryReminderDays: Number(e.target.value) })} /></label>
        {num('draftTtlDays', 'Удалять заброшенные черновики через, дней')}
      </div>
      <h3>Чат покупателей и продавцов</h3>
      <label className="check"><input type="checkbox" checked={f.chatEnabled} onChange={(e) => setF({ ...f, chatEnabled: e.target.checked })} /> Чат в приложении включён (продавец может отключить его у себя в настройках связи)</label>
      <div className="grid2">
        {num('hourlyChatStartLimit', 'Новых переписок в час на пользователя')}
        {num('dailyChatMessageLimit', 'Сообщений в сутки на пользователя')}
      </div>
      <p className="muted small">Переписку видят только участники. Модератор видит её только через жалобу из чата, каждый просмотр пишется в журнал.</p>
      <ChannelSection f={f} setF={setF} />
      <h3>Правовые документы</h3>
      <label className="field">
        Версия правил и политики конфиденциальности
        <input value={f.legalVersion} pattern="[A-Za-z0-9._\-]{1,40}" onChange={(e) => setF({ ...f, legalVersion: e.target.value })} />
      </label>
      <p className="muted">Изменение версии потребует от всех пользователей заново принять правила перед любым действием (публикация, сообщение, жалоба). Меняйте только вместе с публикацией новых текстов.</p>
      <h3>Монетизация</h3>
      <fieldset className="langs">
        <legend>Обозначение валюты в ценах</legend>
        {(['uz', 'kaa', 'ru'] as const).map((l) => (
          <label key={l} className="field">
            {{ uz: 'Узбекский', kaa: 'Каракалпакский', ru: 'Русский' }[l]}
            <input maxLength={12} value={f.currencyLabels[l]} onChange={(e) => setF({ ...f, currencyLabels: { ...f.currencyLabels, [l]: e.target.value } })} />
          </label>
        ))}
        <p className="muted small">Подпись в поле цены при подаче объявления меняется в «Переводах» (ключ common.currency).</p>
      </fieldset>
      <fieldset className="langs">
        <legend>Языки интерфейса Mini App</legend>
        {(['uz', 'kaa', 'ru'] as const).map((l) => (
          <label key={l} className="check">
            <input
              type="checkbox"
              checked={f.enabledLocales.includes(l)}
              // Хотя бы один язык должен оставаться включённым (сервер проверяет то же).
              disabled={f.enabledLocales.length === 1 && f.enabledLocales.includes(l)}
              onChange={(e) => setF({ ...f, enabledLocales: e.target.checked ? [...f.enabledLocales, l] : f.enabledLocales.filter((x) => x !== l) })}
            />{' '}
            {{ uz: 'Узбекский (латиница)', kaa: 'Каракалпакский', ru: 'Русский' }[l]}
          </label>
        ))}
        <p className="muted small">Пользователи с выключенным языком увидят первый включённый язык. Тексты объявлений не переводятся.</p>
      </fieldset>
      <label className="check"><input type="checkbox" disabled={!can('admins.manage')} checked={f.adminTwoFactorRequired} onChange={(e) => setF({ ...f, adminTwoFactorRequired: e.target.checked })} /> Двухфакторный вход обязателен для всех администраторов{can('admins.manage') ? '' : ' (меняет владелец)'}</label>
      <label className="check"><input type="checkbox" checked={f.promotionsEnabled} onChange={(e) => setF({ ...f, promotionsEnabled: e.target.checked })} /> Продвижение включено (ручное назначение)</label>
      <label className="check">
        <input type="checkbox" disabled={!can('tariffs.manage') || !f.promotionsEnabled} checked={f.paymentsEnabled && f.promotionsEnabled} onChange={(e) => setF({ ...f, paymentsEnabled: e.target.checked })} />{' '}
        Пользователи покупают продвижение сами за Telegram Stars
      </label>
      {f.paymentsEnabled && f.promotionsEnabled && !f.promotionTariffs.some((t) => t.type !== 'business' && t.priceStars > 0) ? (
        <p className="warn" role="alert">Оплата включена, но у всех тарифов цена в Stars — 0: покупать нечего, кнопка «Продвинуть» в приложении не появится. Укажите цену хотя бы одному тарифу.</p>
      ) : null}
      <p className="muted small">Цифровые услуги внутри Mini App Telegram разрешает продавать только за Stars. Возврат — в разделе «Платежи». До включения опубликуйте оферту и правила возврата (docs/monetization.md).</p>
      <table>
        <thead><tr><th>Код</th><th>Тип</th><th>Цена, сум (ручные продажи)</th><th>Цена, ⭐ Stars (0 — не продаётся)</th><th>Дней</th></tr></thead>
        <tbody>
          {f.promotionTariffs.map((t, i) => {
            const set = (patch: Partial<typeof t>) => setF({ ...f, promotionTariffs: f.promotionTariffs.map((x, j) => (j === i ? { ...x, ...patch } : x)) });
            return (
              <tr key={t.code}>
                <td><code>{t.code}</code></td>
                <td>{{ bump: 'Поднятие', vip: 'VIP', highlight: 'Выделение', business: 'Бизнес-профиль' }[t.type]}</td>
                <td><input type="number" min={0} disabled={!can('tariffs.manage')} value={t.priceSum} onChange={(e) => set({ priceSum: Number(e.target.value) })} /></td>
                <td>{t.type === 'business' ? '—' : <input type="number" min={0} aria-label={`Stars ${t.code}`} disabled={!can('tariffs.manage')} value={t.priceStars} onChange={(e) => set({ priceStars: Number(e.target.value) })} />}</td>
                <td>{t.type === 'bump' ? '—' : <input type="number" min={1} max={365} disabled={!can('tariffs.manage')} value={t.durationDays} onChange={(e) => set({ durationDays: Number(e.target.value) })} />}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <div className="row"><Btn busy={action.busy} onClick={() => void save()}>Сохранить</Btn>{saved ? <span className="ok">Сохранено</span> : null}</div>
      {action.error ? <ErrorBox error={action.error} /> : null}
    </div>
  );
}
